import { sectionImageKey } from './home-sections';
export const CONTENT_PAGE_STATUSES = ['published', 'draft'];
/** Hard limits the API holds a save to; the admin's guidance is tighter (CONTENT_PAGE_SEO). */
export const CONTENT_PAGE_LIMITS = {
    pages: 50,
    slug: 60,
    title: 120,
    body: 50_000,
    metaTitle: 120,
    metaDescription: 320,
    image: 500,
};
/** What reads well in a search result: Google cuts a title near 60 characters and a description near 160. */
export const CONTENT_PAGE_SEO = {
    title: { min: 30, max: 60 },
    description: { min: 70, max: 160 },
    /** Below this, a page reads as thin to a search engine. */
    words: 300,
};
/** Lower-case words joined by dashes — the address after `/pages/`. */
export const CONTENT_PAGE_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const ENTRY_FIELDS = new Set(['ar', 'en', 'status', 'noindex', 'footer', 'image', 'updatedAt']);
const TEXT_FIELDS = new Set(['title', 'body', 'metaTitle', 'metaDescription']);
const STORAGE_KEY = /^(?!.*\.\.)[A-Za-z0-9][A-Za-z0-9/_.-]*$/;
/**
 * Why a `pages.content` value would not save, or null. The first problem
 * found, named by where it is — `privacy.ar.metaDescription is over 320
 * characters` — so the admin can say which page and which field.
 */
export function contentPagesProblem(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return 'pages.content must be an object of pages by slug.';
    const entries = Object.entries(value);
    if (entries.length > CONTENT_PAGE_LIMITS.pages)
        return `pages.content holds at most ${CONTENT_PAGE_LIMITS.pages} pages.`;
    for (const [slug, entry] of entries) {
        if (slug.length > CONTENT_PAGE_LIMITS.slug || !CONTENT_PAGE_SLUG.test(slug)) {
            return `"${slug}" is not a page address: lower-case letters, digits and dashes, at most ${CONTENT_PAGE_LIMITS.slug}.`;
        }
        if (!entry || typeof entry !== 'object' || Array.isArray(entry))
            return `${slug} must be an object.`;
        const record = entry;
        const unknown = Object.keys(record).find((field) => !ENTRY_FIELDS.has(field));
        if (unknown)
            return `${slug}.${unknown} is not a page field.`;
        for (const language of ['ar', 'en']) {
            const problem = textProblem(`${slug}.${language}`, record[language]);
            if (problem)
                return problem;
        }
        if (record.status !== undefined && !CONTENT_PAGE_STATUSES.includes(record.status)) {
            return `${slug}.status must be "published" or "draft".`;
        }
        for (const flag of ['noindex', 'footer']) {
            if (record[flag] !== undefined && typeof record[flag] !== 'boolean')
                return `${slug}.${flag} must be true or false.`;
        }
        if (record.image !== undefined && record.image !== null && !isPageImage(record.image)) {
            return `${slug}.image must be an uploaded image.`;
        }
    }
    return null;
}
function textProblem(where, value) {
    if (value === undefined)
        return null;
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return `${where} must be an object.`;
    for (const [field, text] of Object.entries(value)) {
        if (!TEXT_FIELDS.has(field))
            return `${where}.${field} is not a page field.`;
        if (typeof text !== 'string')
            return `${where}.${field} must be text.`;
        const limit = CONTENT_PAGE_LIMITS[field];
        if (text.length > limit)
            return `${where}.${field} is over ${limit} characters.`;
    }
    return null;
}
function isPageImage(value) {
    const key = sectionImageKey(value);
    if (key !== null)
        return key.length <= CONTENT_PAGE_LIMITS.image && STORAGE_KEY.test(key);
    return (typeof value === 'string' &&
        value.length <= CONTENT_PAGE_LIMITS.image &&
        (value.startsWith('/') ? !value.startsWith('//') : /^https:\/\//i.test(value)));
}
/** Whether a shopper can open a page: it is not a draft. */
export function isPagePublished(entry) {
    return Boolean(entry) && entry?.status !== 'draft';
}
//# sourceMappingURL=content-pages.js.map